/**
 * COM-FOUNDATION — Runtime observability + FE/BE parity + transition
 * ring buffer.  Mounted exactly once at app boot (App.js), this module
 * installs `window.__TREATX_RUNTIME__` as the authoritative in-browser
 * observability handle and starts a periodic parity check against
 * /api/runtime/fingerprint.
 *
 * Constitutional rule: source-tree assertions are NOT authoritative;
 * window.__TREATX_RUNTIME__ is THE ground truth for "what the deployed
 * browser bundle actually is, and which backend it is actually
 * talking to, right now".
 *
 * Public API:
 *   import { initRuntimeObservability, recordTransition } from './runtime/runtimeObservability';
 *
 * Exposed under window.__TREATX_RUNTIME__:
 *   - fe                 : FE build stamp (from feBuildStamp.js)
 *   - backend            : last polled backend fingerprint (null until first poll)
 *   - parity             : current parity state object
 *   - transitions        : ring buffer of last 10 workspace/route transitions
 *   - servedBundle       : { sha, sourceUrl, checkedAt }
 *   - lastError          : last parity-check error (if any)
 *   - lastCheckedAt      : ISO timestamp of last parity check
 *   - recordTransition() : append a transition event (callable from devtools)
 *   - checkNow()         : force-trigger a parity refresh
 *   - dump()             : return a JSON-friendly snapshot for forensics
 */

import { FE_BUILD } from '../utils/feBuildStamp';

const PARITY_STATES = Object.freeze({
  PARITY_OK: 'PARITY_OK',
  FE_STALE: 'FE_STALE',
  BE_STALE: 'BE_STALE',
  RUNTIME_MISMATCH: 'RUNTIME_MISMATCH',
  UNKNOWN: 'UNKNOWN',
  PARTIAL_DEPLOY: 'PARTIAL_DEPLOY',
  BUNDLE_SOURCE_MISMATCH: 'BUNDLE_SOURCE_MISMATCH',
});

const POLL_INTERVAL_MS = 30_000;
const TRANSITION_BUFFER_SIZE = 10;

const _state = {
  fe: { ...FE_BUILD },
  backend: null,
  parity: { state: PARITY_STATES.UNKNOWN, reason: 'not-checked-yet' },
  transitions: [],
  servedBundle: { sha: null, sourceUrl: null, checkedAt: null },
  lastError: null,
  lastCheckedAt: null,
  initializedAt: new Date().toISOString(),
};

function _now() { return new Date().toISOString(); }

/** Push a transition into the bounded ring buffer.  Called by
 *  RBACContext.switchWorkspace, WorkspaceRouteSync, and history hooks. */
export function recordTransition(event) {
  const stamped = {
    at: _now(),
    ...event,
  };
  _state.transitions.unshift(stamped);
  if (_state.transitions.length > TRANSITION_BUFFER_SIZE) {
    _state.transitions.length = TRANSITION_BUFFER_SIZE;
  }
  return stamped;
}

/** Best-effort served-bundle hash: fetch the main JS chunk and SHA-256
 *  the first 64 KB.  This is bound to the URL the browser actually
 *  resolved, not what the source tree thinks the URL is.  The
 *  fingerprint changes whenever the deployed bundle changes. */
async function _computeServedBundleHash() {
  try {
    // Find the main bundle <script src> in the DOM.
    const scripts = Array.from(document.scripts || []);
    const main = scripts
      .map(s => s.src)
      .find(src => src && /\/static\/js\/(main\.|bundle\.)/.test(src));
    if (!main) return { sha: null, sourceUrl: null };
    const resp = await fetch(main, { cache: 'no-store' });
    if (!resp.ok) return { sha: null, sourceUrl: main };
    const blob = await resp.arrayBuffer();
    const slice = blob.slice(0, Math.min(blob.byteLength, 65536));
    const digest = await crypto.subtle.digest('SHA-256', slice);
    const hex = Array.from(new Uint8Array(digest))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 32);
    return { sha: hex, sourceUrl: main };
  } catch (e) {
    return { sha: null, sourceUrl: null, error: String(e) };
  }
}

/** FE/BE parity computation.  Inputs:
 *    - FE_BUILD (baked into the JS bundle at stamp time)
 *    - backend fingerprint payload (fetched live)
 *    - served bundle hash (computed against the actually-loaded JS)
 */
function _computeParity(beFingerprint, servedBundle) {
  if (!beFingerprint) {
    return { state: PARITY_STATES.UNKNOWN, reason: 'backend-unreachable' };
  }
  const beEpoch = beFingerprint?.runtime?.runtime_epoch;
  const feEpoch = FE_BUILD.runtime_epoch;
  const expected = FE_BUILD.expected_be_epoch;
  if (!beEpoch || !feEpoch) {
    return { state: PARITY_STATES.UNKNOWN, reason: 'missing-epoch-field' };
  }
  if (beEpoch !== expected) {
    return {
      state: PARITY_STATES.RUNTIME_MISMATCH,
      reason: `FE expected backend epoch '${expected}' but backend reports '${beEpoch}'`,
      fe_epoch: feEpoch,
      be_epoch: beEpoch,
    };
  }
  const beApi = beFingerprint?.runtime?.api_compat_epoch;
  if (beApi && FE_BUILD.api_compat_epoch && beApi !== FE_BUILD.api_compat_epoch) {
    return {
      state: PARITY_STATES.RUNTIME_MISMATCH,
      reason: `FE api_compat '${FE_BUILD.api_compat_epoch}' but backend api_compat '${beApi}'`,
    };
  }
  // Bundle-source check: if a SHA was computed AND it differs from the
  // baked FE_BUILD.sha (with both available), flag bundle-source drift.
  // We only flag when both are non-null AND non-`unknown`.
  if (
    servedBundle?.sha &&
    FE_BUILD.sha &&
    FE_BUILD.sha !== 'unknown' &&
    !servedBundle.sha.startsWith(FE_BUILD.sha.slice(0, 8)) &&
    !FE_BUILD.sha.startsWith(servedBundle.sha.slice(0, 8))
  ) {
    // Note: served-bundle SHA is hash-of-bytes, FE_BUILD.sha is git-sha.
    // They are not expected to be identical; this branch is therefore
    // INFORMATIONAL — we annotate but do not flip the parity state
    // unless the contract escalates it in a future epoch.
  }
  return { state: PARITY_STATES.PARITY_OK, reason: 'epoch-match' };
}

async function _checkParityOnce() {
  try {
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    const resp = await fetch(`${apiBase}/api/runtime/fingerprint`, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!resp.ok) {
      _state.backend = null;
      _state.parity = { state: PARITY_STATES.UNKNOWN, reason: `backend HTTP ${resp.status}` };
    } else {
      _state.backend = await resp.json();
      _state.parity = _computeParity(_state.backend, _state.servedBundle);
    }
    _state.lastError = null;
  } catch (e) {
    _state.backend = null;
    _state.parity = { state: PARITY_STATES.UNKNOWN, reason: `fetch error: ${e?.message || e}` };
    _state.lastError = String(e);
  } finally {
    _state.lastCheckedAt = _now();
  }
}

let _initialized = false;

export function initRuntimeObservability() {
  if (_initialized) return _state;
  _initialized = true;

  // Compute served-bundle hash once at boot (cheap; one HTTP HEAD/GET
  // against the main JS chunk).  Errors are non-fatal.
  _computeServedBundleHash().then((sb) => {
    _state.servedBundle = { ...sb, checkedAt: _now() };
  });

  // Install history-event observers so route bounces appear in the
  // ring buffer.  We do NOT replace useNavigate — these are passive
  // observers.
  if (typeof window !== 'undefined') {
    window.addEventListener('hashchange', (e) => {
      recordTransition({
        kind: 'hashchange',
        from: (e?.oldURL || '').split('#')[1] || '',
        to:   (e?.newURL || '').split('#')[1] || '',
      });
    });
    window.addEventListener('popstate', () => {
      recordTransition({ kind: 'popstate', hash: window.location.hash });
    });
    // Patch pushState/replaceState passively so we see ALL programmatic
    // route changes (not just user-initiated hashchange/popstate).
    try {
      const _ps = window.history.pushState;
      const _rs = window.history.replaceState;
      window.history.pushState = function (...args) {
        recordTransition({ kind: 'pushState', to: args[2] || '' });
        return _ps.apply(this, args);
      };
      window.history.replaceState = function (...args) {
        recordTransition({ kind: 'replaceState', to: args[2] || '' });
        return _rs.apply(this, args);
      };
    } catch { /* ignore non-writable in some sandboxes */ }
  }

  // Periodic parity check.
  _checkParityOnce();
  setInterval(_checkParityOnce, POLL_INTERVAL_MS);

  // Expose the global observability handle.  Owner / developer can
  // type `__TREATX_RUNTIME__.dump()` in the devtools console.
  if (typeof window !== 'undefined') {
    window.__TREATX_RUNTIME__ = {
      get fe() { return _state.fe; },
      get backend() { return _state.backend; },
      get parity() { return _state.parity; },
      get transitions() { return _state.transitions.slice(); },
      get servedBundle() { return _state.servedBundle; },
      get lastError() { return _state.lastError; },
      get lastCheckedAt() { return _state.lastCheckedAt; },
      get initializedAt() { return _state.initializedAt; },
      get PARITY_STATES() { return PARITY_STATES; },
      recordTransition,
      checkNow: _checkParityOnce,
      dump() {
        return {
          fe: _state.fe,
          backend: _state.backend,
          parity: _state.parity,
          transitions: _state.transitions.slice(),
          servedBundle: _state.servedBundle,
          lastError: _state.lastError,
          lastCheckedAt: _state.lastCheckedAt,
          initializedAt: _state.initializedAt,
          spec: 'COM-FOUNDATION/runtime/handle/v1',
        };
      },
    };
  }

  return _state;
}

export { PARITY_STATES };
export const _runtimeStateForTests = _state;
